import {
    Avatar,
    Box,
    Link as ChakraLink,
    Fade,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Text,
    useColorMode,
    useColorModeValue,
    useMediaQuery,
} from '@chakra-ui/react'
import { Link, useMatches, useSubmit } from '@remix-run/react'
import { BiExit, BiMoon, BiSun, BiUser } from 'react-icons/bi'
import { GiHamburgerMenu } from 'react-icons/gi'
import type { CurrentUserJson } from '~/services/auth/auth.server'
import { PHOCUSLogo } from '../lib/svg/PHOCOSLogo'
import { Limiter } from './Limiter'
import { LinkButton } from './LinkButton'

const TopLinkComponent: React.FC<{ title: string; link: string; active?: boolean }> = ({ title, link, active }) => {
    return (
        <ChakraLink
            fontWeight="bold"
            as={Link}
            to={link}
            p={2}
            px={8}
            rounded="md"
            textAlign="center"
            textDecoration="none"
            bg={active ? 'purple.500' : undefined}
            color={active ? 'white' : undefined}
            _hover={{
                bg: 'purple.500',
                color: 'white',
                textDecoration: 'none',
            }}
        >
            {title}
        </ChakraLink>
    )
}

interface Props {
    user: CurrentUserJson | null
    canCreate: boolean
    showAdmin: boolean
    env: string
}

export function Nav(props: Props) {
    const submit = useSubmit()
    const matches = useMatches()
    const { colorMode, toggleColorMode } = useColorMode()
    const [sm] = useMediaQuery('(max-width: 1200px)')
    const isDark = colorMode === 'dark'

    return (
        <Box as="nav" borderBottom="1px solid" borderColor={useColorModeValue('gray.100', 'gray.700')}>
            <Limiter
                display="flex"
                transition="200ms all"
                py={{ base: 4, md: 3 }}
                px={4}
                bg={useColorModeValue('white', 'gray.800')}
                justifyContent="space-between"
                alignItems="center"
                w="100%"
            >
                {/* Left link list */}

                <HStack spacing={0}>
                    <Link to="/">
                        <PHOCUSLogo />
                    </Link>
                </HStack>

                <HStack spacing={8}>
                    {!sm ? (
                        <>
                            {props.canCreate ? (
                                <TopLinkComponent
                                    title="New Submission"
                                    link="/submit/new"
                                    active={matches.some((match) => match.pathname === '/submit/new')}
                                />
                            ) : null}
                            {props.user && (
                                <TopLinkComponent
                                    title="All Submissions"
                                    link="/submit/list"
                                    active={matches.some((match) => match.pathname === '/submit/list')}
                                />
                            )}
                            {props.user && (
                                <TopLinkComponent
                                    title="Snapshot"
                                    link="/snapshot"
                                    active={matches.some((match) => match.pathname === '/snapshot')}
                                />
                            )}
                            {/* <TopLinkComponent title="PHN Admin" link="/admin/phn" /> */}
                            {props.showAdmin && (
                                <TopLinkComponent
                                    title="Admin"
                                    link="/admin/platform"
                                    active={matches.some((match) => match.pathname === '/admin/platform')}
                                />
                            )}
                        </>
                    ) : null}

                    {/* Right link list */}

                    {!props.user && (
                        <Fade in>
                            <HStack spacing={4} display={{ base: 'none', md: 'flex' }}>
                                <LinkButton to="/login" variant="ghost">
                                    Login
                                </LinkButton>
                            </HStack>
                        </Fade>
                    )}

                    {/* Right menu list */}
                    {/* https://github.com/chakra-ui/chakra-ui/issues/3173 */}
                    <Box mr={4}>
                        <Menu placement="bottom-end" closeOnSelect closeOnBlur>
                            <MenuButton
                                as={IconButton}
                                display={{ base: 'flex', md: props.user ? 'flex' : 'none' }}
                                p={0}
                                colorScheme={props.user ? 'gray' : undefined}
                                borderRadius="full"
                                icon={
                                    props.user ? (
                                        <Avatar
                                            size="sm"
                                            color="black"
                                            boxSize="35px"
                                            bg="purple.50"
                                            // src={createImageUrl(props.user.avatar)}
                                            name={props.user.firstName}
                                        />
                                    ) : (
                                        <Box as={GiHamburgerMenu} />
                                    )
                                }
                            />

                            <MenuList fontSize="md">
                                {props.user ? (
                                    <>
                                        <Link to="/settings">
                                            <MenuItem icon={<Box as={BiUser} boxSize="16px" />}>Settings</MenuItem>
                                        </Link>
                                        {/* {props.user.role === Role.ADMIN && (
                                        <Link to="/admin">
                                            <MenuItem icon={<Box as={BiCog} boxSize="16px" />}>Admin</MenuItem>
                                        </Link>
                                    )} */}
                                        <MenuDivider />
                                        <MenuItem
                                            closeOnSelect={false}
                                            icon={<Box as={isDark ? BiSun : BiMoon} boxSize="16px" />}
                                            onClick={toggleColorMode}
                                        >
                                            {`Switch to ${isDark ? 'light' : 'dark'}`}
                                        </MenuItem>
                                        <MenuDivider />

                                        <MenuItem
                                            onClick={() => submit(null, { method: 'post', action: '/logout' })}
                                            icon={<Box as={BiExit} boxSize="16px" />}
                                        >
                                            Logout
                                        </MenuItem>
                                    </>
                                ) : (
                                    <>
                                        <MenuItem
                                            closeOnSelect={false}
                                            icon={<Box as={isDark ? BiSun : BiMoon} boxSize="16px" />}
                                            onClick={toggleColorMode}
                                        >
                                            Toggle theme
                                        </MenuItem>
                                        <MenuDivider />
                                        <Link to="/login">
                                            <MenuItem>Login</MenuItem>
                                        </Link>
                                    </>
                                )}
                            </MenuList>
                        </Menu>
                    </Box>
                </HStack>
            </Limiter>
            {props.env !== 'PROD' && (
                <Box bg="#BA68C8" py={1} mx={8} rounded="lg" fontSize="sm">
                    <Text color="white" textAlign="center">
                        This is a{' '}
                        <Text as="span" fontWeight="bold">
                            {props.env}
                        </Text>{' '}
                        environment. Submissions made here will not be sent to DHDA. Please use the{' '}
                        <ChakraLink
                            isExternal
                            fontWeight="bold"
                            textDecoration="underline"
                            href="https://phocus.primaryhealthinsights.org.au/"
                        >
                            PHOCUS production environment
                        </ChakraLink>{' '}
                        to finalise PHN submissions.
                    </Text>
                </Box>
            )}
        </Box>
    )
}
